// src/components/DynamicForms/DynamicFormComponents/FormInput.tsx

import React from "react";
import Select from "react-select";
import {
  FormConfig,
  QuestionnaireItem,
} from "../../../../interfaces/dynamicForms/Forms";
import { normalizeOptions, SelectOption } from "./utils";
import { Form } from "react-bootstrap";
import './FormInput.css';

type DynamicOptionsMap = Record<string, SelectOption[]>;

interface FormInputProps {
  q: QuestionnaireItem;
  value: any;
  onChange: (val: any) => void;
  dynamicOptions: DynamicOptionsMap;
  formConfig: FormConfig;
  participantId?: string;
  autoPopulateData?: Record<string, any>;
}

export const FormInput: React.FC<FormInputProps> = ({
  q,
  value,
  onChange,
  dynamicOptions,
  formConfig,
  participantId,
  autoPopulateData,
}) => {
  const dynamic = dynamicOptions[q.id];
  const staticOptions = q.options;

  const currentTextValue = typeof value === "string" ? value : "";
  const currentLength = currentTextValue.length;

  const charLimitInfoId =
    q.maxLength != null ? `${q.id}-char-limit-info` : undefined;

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;
    const numericValue = inputValue.replace(/[^0-9.]/g, "");
    const parts = numericValue.split(".");
    let cleanedValue = numericValue;
    if (parts.length > 2) {
      cleanedValue = parts[0] + "." + parts.slice(1).join("");
    }
    if (parts[1] && parts[1].length > 2) {
      cleanedValue = parts[0] + "." + parts[1].substring(0, 2);
    }
    if (
      cleanedValue.length > 1 &&
      cleanedValue.startsWith("0") &&
      !cleanedValue.startsWith("0.")
    ) {
      cleanedValue = cleanedValue.substring(1);
    }
    onChange(cleanedValue);
  };

  const handleTelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const rawDigits = inputValue.replace(/\D/g, "");
    const cleanedDigits = rawDigits.slice(0, 10);
    onChange(cleanedDigits);
  };

  // --- Disabling Logic ---

  const isDisabledByParticipant =
    !!participantId && q.dynamicSource === "participants";

  const isDisabledByTaskContactType =
    !!autoPopulateData?.contactType &&
    (q.dynamicSource === "taskContactTypes" ||
      q.label.toLowerCase().includes("contact type"));

  // UPDATED: Check for waiverType object AND waiverName
  const hasWaiverData = 
      !!autoPopulateData?.waiverType || 
      !!autoPopulateData?.waiverName || 
      (!!autoPopulateData?.waiverType && typeof autoPopulateData.waiverType === 'object' && !!autoPopulateData.waiverType.waiverName);

  const isDisabledByWaiverType =
    hasWaiverData &&
    (q.dynamicSource === "waiverTypes" || q.label.toLowerCase().includes("waiver"));

  const isDisabledByScid =
    !!autoPopulateData?.scid &&
    (q.id.toLowerCase().includes("scid") ||
      q.label.toLowerCase().includes("scid") ||
      q.label.toLowerCase().includes("support coordinator"));

  const isDisabled =
    !!(q.disabled ?? false) ||
    isDisabledByParticipant ||
    isDisabledByTaskContactType ||
    isDisabledByWaiverType ||
    isDisabledByScid;

  switch (q.type) {
    case "text":
      return (
        <>
          <input
            type="text"
            className={`${q.disabled ? "" : ""}`}
            aria-describedby={charLimitInfoId}
            maxLength={q.maxLength}
            disabled={isDisabled}
            placeholder={q.placeholder ?? "Enter text here..."}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
          {q.maxLength != null && (
            <div
              id={charLimitInfoId}
              style={{
                fontSize: "0.8em",
                marginTop: "4px",
                textAlign: "right",
              }}
              aria-live="off"
              className={`${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-[#198754]'}`}
            >
              {currentLength} / {q.maxLength} characters
            </div>
          )}
        </>
      );
    case "paragraph": {
        const initialRows = 3;
        const maxRows = 10;
        const estimatedLineHeight = 24;
        const verticalPadding = 16;
        const borderHeight = 2;
        const estimatedMaxHeight =
          maxRows * estimatedLineHeight + verticalPadding + borderHeight;
  
        const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
          const textarea = event.target;
          textarea.style.height = "auto";
          const scrollHeight = textarea.scrollHeight;
          textarea.style.height = `${Math.min(
            scrollHeight,
            estimatedMaxHeight
          )}px`;
          onChange(textarea.value);
        };
   
        return (
          <>
            <textarea
              id={q.id}
              className={` ${q.disabled ? "" : ""} `}
              style={{ boxSizing: "border-box" }}
              maxLength={q.maxLength}
              aria-describedby={charLimitInfoId}
              disabled={isDisabled}
              placeholder={q.placeholder ?? "Enter text here..."}
              value={currentTextValue}
              onChange={handleInput}
            />
            {q.maxLength != null && (
              <div
                id={charLimitInfoId}
                style={{
                  fontSize: "0.8em",
                  marginTop: "4px",
                  color: "#6c757d",
                  textAlign: "right",
                }}
                aria-live="off"
              >
                {currentLength} / {q.maxLength} characters
              </div>
            )}
          </>
        );
      }
      case "radio": {
        const finalOptions: SelectOption[] = Array.isArray(q.options)
          ? (q.options as Array<string | SelectOption>).map((o) =>
            typeof o === "string" ? { value: o, label: o } : o
          )
          : [];
  
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {finalOptions.map((opt) => {
              const selected = value === opt.value;
              return (
                <div
                  key={opt.value}
                  onClick={() => onChange(opt.value)}
                  className={`
                                      cursor-pointer rounded-lg px-4 py-3 border text-sm
                                      ${
                                        selected
                                          ? "bg-gray-100 border-[#198754] text-gray-800"
                                          : "bg-white border-gray-300 text-gray-600"
                                      }
                                      transition-colors
                                      `}
                >
                  {opt.label}
                </div>
              );
            })}
          </div>
        );
      }
  
      case "react-select": {
        let finalOptions: SelectOption[] = [];
  
        if (dynamic && dynamic.length > 0) {
          finalOptions = dynamic;
        } else {
          finalOptions = normalizeOptions(staticOptions);
        }
              
        return (
          <div className="react-select-wrapper remove-input-txt-border text-gray-800">
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              isMulti={q.isMulti}
              options={finalOptions}
              // REVERTED: Standard logic, no synthetic fallback
              value={
                q.isMulti
                  ? finalOptions.filter((o) => (value || []).includes(o.value))
                  : finalOptions.find((o) => o.value === value) || null
              }
              onChange={(selected) => {
                if (q.isMulti) {
                  onChange((selected as SelectOption[]).map((s) => s.value));
                } else {
                  onChange(selected ? (selected as SelectOption).value : null);
                }
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed" 
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              isDisabled={isDisabled}
            />
          </div>
        );    
    }
  
      case "time":
        return (
          <input
            type="time"
            className=""
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={isDisabled}
          />
        );
  
      case "date":
        return (
          <input
            type="date"
            className=""
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={isDisabled}
          />
        );
  
      case "datetime":
        return (
          <input
            type="datetime-local"
            className=""
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={isDisabled}
          />
        );
  
      case "checkbox":
        return (
          <label
            className="inline-flex items-center text-sm text-gray-800 mb-0"
            style={{ gap: ".375rem" }}
          >
            <Form.Check
              type="checkbox"
              className="mt-0"
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              disabled={isDisabled}
            />
            <span>{q.label}</span>
          </label>
        );
  
      case "number":
        return (
          <input
            type="number"
            id={q.id}
            className={`${q.disabled ? "" : ""}`}
            min={q.minNumber}
            max={q.maxNumber}
            step={q.stepNumber ?? "any"} 
            disabled={isDisabled}
            placeholder={q.placeholder ?? "Enter a number..."}
            value={value ?? ""} 
            onChange={(e) => onChange(e.target.value)}
          />
        );
  
      case "currency": {
        const symbol = q.currencySymbol ?? "$";
        const paddingLeft = symbol ? "pl-7" : "pl-4";
  
        return (
          <div className="relative w-full">
            {symbol && (
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 pointer-events-none">
                {symbol}
              </span>
            )}
            <input
              type="text" 
              inputMode="decimal"
              id={q.id}
              className={`
                              ${paddingLeft}
                              ${q.disabled ? "" : ""}
                              focus:outline-none
                              focus:ring-0
                              focus:border-[#198754]
                          `}
              min={q.minNumber}
              max={q.maxNumber}
              step={q.stepNumber}
              disabled={isDisabled}
              placeholder={q.placeholder ?? `0.00`}
              value={value ?? ""} 
              onChange={handleCurrencyChange} 
              onBlur={(e) => { }}
            />
          </div>
        );
      }
  
      case "tel":
        return (
          <input
            type="tel"
            inputMode="tel"
            id={q.id}
            className={`${q.disabled ? "" : ""}`}
            maxLength={q.maxLength} 
            disabled={isDisabled}
            placeholder={q.placeholder ?? "e.g., (555) 123-4567"} 
            value={value || ""} 
            onChange={handleTelChange}
          />
        );
  
      default:
        return null;
  }
};